<template>
  <div
    class="histogram"
    ref="histogram"
    :style="{ height: HISTOGRAM_HEIGHT + 'px' }"
  >
    <div
      v-for="range in rangesConverted"
      :style="{
        left: range[0] + '%',
        width: barWidth,
        height: countToHeight(range[2]) + 'px',
      }"
      class="histogram-marker"
      :class="{
        'histogram-marker-selected':
          range[0] >= valueToHistogramPercent(selectedRange[0]) &&
          range[1] <=
            roundUpToTwoDecimalPlace(valueToHistogramPercent(selectedRange[1])),
      }"
    ></div>
  </div>
</template>

<script setup lang="ts">
import type { PriceHistogram } from '~/apps/search/schemas'

const props = defineProps<{
  priceHistogramData: PriceHistogram
  sliderMinPrice: number
  sliderMaxPrice: number
  selectedRange: number[]
}>()

const HISTOGRAM_HEIGHT = 100
const barWidth = computed(() => `${100 / props.priceHistogramData.num_bins}%`)

const maxCount = computed(() => {
  return Math.max(...Object.values(props.priceHistogramData.counts))
})

function countToHeight(count) {
  const height = (HISTOGRAM_HEIGHT / maxCount.value) * count
  return height ? height + 2 : 0
}

const ranges = computed(() => {
  return props.priceHistogramData?.bins
    .map((el, idx, arr) => {
      if (arr[idx + 1]) {
        // bin start value, bin end value, bin count
        return [el, arr[idx + 1], props.priceHistogramData.counts[idx]]
      }
    })
    .filter((el) => el) as [[number, number, number]]
})

const oneHundredPercent = computed(
  () => props.sliderMaxPrice - props.sliderMinPrice,
)
const onePercent = computed(() => 100 / oneHundredPercent.value)

const rangesConverted = computed(() => {
  if (!ranges) return
  return ranges.value.map((range) => [
    (range[0] * 100 - props.sliderMinPrice) * onePercent.value,
    (range[1] * 100 - props.sliderMinPrice) * onePercent.value,
    range[2],
  ])
})

function roundUpToTwoDecimalPlace(num) {
  return Math.ceil(num * 100) / 100
}

function valueToHistogramPercent(value) {
  return (value - props.sliderMinPrice) * onePercent.value
}
</script>
<style scoped>
.filter-sort-price-slider-inputs {
  display: flex;
  column-gap: 3em;
  justify-content: space-between;
  margin: 0.75em auto;
}

label {
  font-weight: normal;
}

.filter-sort-price-slider-inputs div {
  display: flex;
  align-items: baseline;
}

.filter-sort-price-slider-inputs input {
  width: 100%;
  border: none;
  padding: 0;
  font-size: 1em;
  margin-left: 0.2em;
}

.histogram {
  margin: 0 18px;
  margin-bottom: -14px;
  position: relative;
}

.histogram-marker {
  position: absolute;
  bottom: 0;
  background-color: #ccc;
  border-top-left-radius: 2px;
  border-top-right-radius: 2px;
}

.histogram-marker-selected {
  background-color: #3498db;
}
</style>
